import { Action, Middleware, ThunkAction, configureStore } from '@reduxjs/toolkit';
import viewsReducer from '../pages/redux';
import appReducer from 'pages/Hospitality/redux';
import homepageReducer from 'pages/SignIn/redux';
import logsReducer from 'pages/Logs/redux';
import metricsReducer from 'pages/Metrics/redux';
import reportingReducer from 'pages/Reporting/redux';
import settingsReducer from 'pages/Settings/redux';
import reserveRoomsReducer from 'pages/ReserveRooms/redux';
import globalReducer, { callEndpoint } from './global';

/**
 * Keep IndexedDB hotel cache aligned with Redux after local hotel updates
 * (credits, inventory, plans, etc.) so later cache hits are not stale.
 */
const hotelDataIndexedDbSync: Middleware = () => (next) => (action) => {
  const result = next(action);
  if (
    action
    && typeof action === 'object'
    && 'type' in action
    && (action as { type?: string }).type === 'views/setHotelData'
    && (action as { payload?: unknown }).payload
  ) {
    // Defer so the reducer commit is visible to store.getState().
    // Hotel-only: full syncAppDbCache would write settings with users:null and
    // poison the info cache (Unresolved staff until System refresh).
    queueMicrotask(() => {
      // Lazy require avoids circular init (store ↔ dbIndexedCache)
      // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
      const { refreshHotelCacheFromState } = require('utils/dbIndexedCache') as typeof import('utils/dbIndexedCache');
      // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
      const { store: appStore } = require('redux/store') as typeof import('redux/store');
      refreshHotelCacheFromState(appStore.getState());
    });
  }
  return result;
};

export const store = configureStore({
  reducer: {
    homepage: homepageReducer,
    views: viewsReducer,
    global: globalReducer,
    app: appReducer,
    logs: logsReducer,
    metrics: metricsReducer,
    reporting: reportingReducer,
    settings: settingsReducer,
    reserveRooms: reserveRoomsReducer
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware({
    thunk: {
      extraArgument: { callEndpoint },
    },
    serializableCheck: false,
  }).concat(hotelDataIndexedDbSync),
});

export type RootState = ReturnType<typeof store.getState>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
